import React, { useEffect, useRef } from 'react';
import clsx from 'clsx';
import { isBoolean, isNotNullable } from 'src/utils/typeGuards';
import { objectEntries } from 'src/utils/objectEntries';
import { objectFromEntries } from 'src/utils/objectFromEntries';
import { Field, FieldGroup, isFieldGroup } from './types/Field';
import { Operator } from './types/Operator';
import { FieldRenderer } from './FieldRenderer';
import { AnyCriteria, CriteriaValue } from '../../types/AlertConfiguration';
import alertCriteriaConfig from './alertCriteriaConfig.json';
import styles from './AlertCriteria.module.less';

enum RenderStrategy {
  oneField = 'one-at-a-time',
  allFields = 'all-at-once',
}

type ValidationDetails = Partial<{
  [K in keyof CriteriaValue]: boolean;
}>;

export class ValidationError {
  readonly details: Readonly<ValidationDetails>;
  readonly message?: string;

  constructor(details: ValidationDetails, message?: string) {
    this.details = details;
    this.message = message;
  }
}

export class DuplicateFoundError {
  message = 'Duplicate criteria is not allowed. Please review and update your criteria.';
}

export type CriteriaError = ValidationError | DuplicateFoundError;

type AlertCriteriaConfig = {
  fields: Array<Field | FieldGroup>;
  renderOptions: {
    strategy: RenderStrategy;
  };
};

export type AlertCriteriaProps = {
  criteria: AnyCriteria;
  error?: CriteriaError | null;
  onCriteriaChange: (criteria: AnyCriteria, error: ValidationError | null) => void;
};

class CriteriaField {
  private readonly isVisible: Field.IsVisible[];

  public readonly config: Field;

  constructor(config: Field, groupIsVisible?: Field.IsVisible) {
    this.config = config;
    this.isVisible = [groupIsVisible, config.isVisible].filter(isNotNullable);
  }

  isVisibleTruthy(values: Partial<CriteriaValue>): boolean {
    return this.isVisible.every((expr: Field.IsVisible) => {
      if (isBoolean(expr)) {
        return expr;
      }
      return Operator.calculateExpression(expr, values);
    });
  }

  isValueEmpty(values: Partial<CriteriaValue>): boolean {
    return isValueEmpty(values[this.config.name]);
  }

  isValueValid(values: Partial<CriteriaValue>): boolean {
    return isFieldValid(this, values[this.config.name]);
  }

  isPrevFieldRendered(fields: CriteriaField[], values: Partial<CriteriaValue>): boolean {
    const index = fields.indexOf(this);
    if (index < 0) {
      throw new Error(`The field "${this.config.name}" is not found in fields array.`);
    }
    const prev = fields[index - 1];
    if (!prev) {
      // the first field should be visible by default
      return true;
    }
    if (prev.isVisibleTruthy(values)) {
      return !prev.isValueEmpty(values) && prev.isPrevFieldRendered(fields, values);
    }
    return prev.isPrevFieldRendered(fields, values);
  }
}

const isValueEmpty = (value?: ValueOf<CriteriaValue>): boolean => {
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return value === undefined;
};

const isFieldValid = (field: CriteriaField, value?: ValueOf<CriteriaValue>): boolean => {
  // TODO: typings should be fixed
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (field.config.validation) {
    const regex = new RegExp(field.config.validation);
    return regex.test(String(value ?? ''));
  }
  return value !== undefined;
};

const getFieldError = (fieldName: keyof CriteriaValue, error?: CriteriaError | null): boolean => {
  if (error instanceof ValidationError) {
    return !!error.details[fieldName];
  }
  if (error instanceof DuplicateFoundError) {
    return true;
  }
  return false;
};

const validate = (fields: CriteriaField[], values: Partial<CriteriaValue>): ValidationError | null => {
  const isFieldRendered = (field: CriteriaField): boolean => {
    return field.isVisibleTruthy(values) && field.isPrevFieldRendered(fields, values);
  };
  const getKeyWithError = (field: CriteriaField): [keyof CriteriaValue, boolean] => {
    return [field.config.name, !field.isValueValid(values)];
  };
  const details = objectFromEntries(fields.filter(isFieldRendered).map(getKeyWithError));
  if (Object.values(details).some(Boolean)) {
    return new ValidationError(details);
  }
  return null;
};

const customValidate = (fields: CriteriaField[], values: Partial<CriteriaValue>): ValidationError | null => {
  // TODO: this is a temporary hardcoded fix of https://ql2.atlassian.net/browse/UI-2763 for the upcoming release
  // and after the release it should be reconsidered as a configurable validation with dependent fields
  // @Dipanshu Ahuja, @Meena Bisht: we should discuss the format of such validation and how to store it in alertCriteriaConfig.json
  const isNegativeAllowed = () => {
    const strUnit = String(values.unit ?? '');
    const strValue = String(values.value ?? '').trim();
    return strUnit === '%' || strValue[0] !== '-';
  };

  const result = validate(fields, values);

  if (!isNegativeAllowed()) {
    return new ValidationError(
      { ...result?.details, value: true },
      'Negative values are not allowed for "Dollars ($)" unit'
    );
  }

  return result;
};

const clearEmptyFields = (
  validationResult: ValidationError | null,
  values: Partial<CriteriaValue>
): ValidationError | null => {
  if (validationResult) {
    const { details } = validationResult;
    const isNotEmpty = ([key, _]: [keyof CriteriaValue, unused]) => !isValueEmpty(values[key]);
    return new ValidationError(
      objectFromEntries(objectEntries(details).filter(isNotEmpty)), //
      validationResult.message
    );
  }
  return null;
};

const getDependentFields = (fields: CriteriaField[], fieldName: keyof CriteriaValue): (keyof CriteriaValue)[] => {
  return fields
    .map(({ config }) => {
      const { url } = config.initialize?.options ?? {};
      return url && url.includes(`\${${fieldName}}`) ? config.name : null;
    })
    .filter(isNotNullable);
};

export const AlertCriteria: React.FC<AlertCriteriaProps> = ({ criteria, error, onCriteriaChange }) => {
  const criteriaRef = useRef<HTMLDivElement>(null);

  // TODO we should use Ajv JSON schema validator instead of TS assertion.
  const criteriaConfig: AlertCriteriaConfig = alertCriteriaConfig as AlertCriteriaConfig;

  const criteriaFields: CriteriaField[] = criteriaConfig.fields.flatMap((config: Field | FieldGroup) =>
    isFieldGroup(config)
      ? config.fields.map((item) => new CriteriaField(item, config.isVisible))
      : new CriteriaField(config)
  );

  const handleBlur = () => {
    onCriteriaChange(criteria, customValidate(criteriaFields, criteria));
  };

  const handleChange = (field: CriteriaField, value: ValueOf<CriteriaValue>) => {
    const { config } = field;
    const resetFields = Object.fromEntries(
      getDependentFields(criteriaFields, config.name).map((key) => [key, undefined])
    );
    const updatedCriteria = { ...criteria, [config.name]: value, ...resetFields };
    const validationResult = customValidate(criteriaFields, updatedCriteria);

    onCriteriaChange(updatedCriteria, clearEmptyFields(validationResult, updatedCriteria));
  };

  const isOneFieldStrategy = (): boolean => {
    return criteriaConfig.renderOptions.strategy === RenderStrategy.oneField;
  };

  const isAllFieldsStrategy = (): boolean => {
    return criteriaConfig.renderOptions.strategy === RenderStrategy.allFields;
  };

  const renderField = (field: CriteriaField) => {
    const { config } = field;
    return (
      <div className={clsx(styles.xRow, config?.lineBreak && styles.fullWidth)} key={config.name}>
        {config?.text && <h4 className={styles.textCriteria}>{config?.text}</h4>}
        <FieldRenderer
          config={config}
          error={getFieldError(config.name, error)}
          value={criteria[config.name]}
          criteria={criteria}
          onChange={(value) => handleChange(field, value)}
        />
      </div>
    );
  };

  const render = () =>
    criteriaFields.map((field: CriteriaField) => {
      if (field.isVisibleTruthy(criteria)) {
        if (isOneFieldStrategy() && field.isPrevFieldRendered(criteriaFields, criteria)) {
          return renderField(field);
        }
        if (isAllFieldsStrategy()) {
          return renderField(field);
        }
      }
    });

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    criteriaRef.current?.focus();
  }, []);

  return (
    <div className={styles.root} ref={criteriaRef} tabIndex={-1} onBlur={handleBlur}>
      {render()}
      {!!error?.message && <p className={styles.errorMessage}>{error.message}</p>}
    </div>
  );
};
